import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { of } from 'rxjs'
import { createAddSignersForm, getSignersFormData } from '../components/add-roles-form/add-roles-form.component'
import {
  createPlaceFieldsForm,
  PlaceFieldsFormComponent,
} from '../components/place-fields-form/place-fields-form.component'
import {
  createTemplateDetailsForm,
  getTemplateDetailsFormData,
} from '../components/template-details-form/template-details-form.component'
import { createTemplateBasicForm } from '../components/upload-template/upload-template.component'
import { PreviewTemplateDrawerService } from '../preview-template/preview-template-drawer.service'
import {
  ContractTemplate,
  CreateContractTemplate,
  CreateContractTemplateComplete,
  SignatureFileService,
  TemplateTypes,
  TemplateUploadResponse,
} from '@tokeet-frontend/signature'
import { ActionFailed, Destroyable, Toaster, untilDestroy, validateForm } from '@tokeet-frontend/tv3-platform'
import { AuthService } from '@tv3/services/auth.service'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { SignatureTemplateType } from '@tv3/containers/inquiries/overlays/inquiry-overlay/automata-booking-overlay/store/signature-template'
import { MatStepper } from '@angular/material/stepper'

enum CreateTemplateSteps {
  SelectTemplate,
  AddSigner,
  PlaceFields,
  TemplateDetails,
}

@Component({
  selector: 'app-create-template-drawer',
  templateUrl: './create-template-drawer.component.html',
  styleUrls: ['./create-template-drawer.component.scss'],
})
export class CreateTemplateDrawerComponent extends Destroyable implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper
  steps = CreateTemplateSteps

  @ViewChild('placeFieldsFormCom') placeFieldsFormCom: PlaceFieldsFormComponent

  templateBasicForm = createTemplateBasicForm(this.fb)
  addSignersForm = createAddSignersForm(this.fb)
  fieldsForm = createPlaceFieldsForm(this.fb)
  templateDetailsForm = createTemplateDetailsForm(this.fb)

  mergedTemplateFileUrl: string
  isProcessing = false

  get templateType(): TemplateTypes {
    return this.templateBasicForm.get('type').value
  }

  get roles() {
    return lodash.map(this.addSignersForm.getRawValue(), 'role')
  }

  get fields() {
    return this.fieldsForm.get('fields').value
  }

  get currentStep() {
    return this.stepper?.selectedIndex
  }

  isTemplateTypeEditable = true

  constructor(
    private dialog: MatDialogRef<CreateTemplateDrawerComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private toaster: Toaster,
    private authService: AuthService,
    private fileService: SignatureFileService,
    private previewDrawer: PreviewTemplateDrawerService,
    @Inject(MAT_DIALOG_DATA) private data: { type?: SignatureTemplateType }
  ) {
    super()
    if (this.data.type) {
      this.templateBasicForm.patchValue({ type: this.data.type })
      this.isTemplateTypeEditable = false
    }
  }

  ngOnInit(): void {
    this.actions.pipe(ofType(CreateContractTemplateComplete), untilDestroy(this)).subscribe(({ item }) => {
      this.dialog.close(item.id)
    })
  }

  close() {
    this.dialog.close()
  }

  nextStep() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }
    if (this.currentStep === CreateTemplateSteps.SelectTemplate) {
      this.isProcessing = true
      this.mergeTemplates().subscribe({
        next: (url) => {
          this.mergedTemplateFileUrl = url
          this.stepper.next()
          this.isProcessing = false
        },
        error: (error) => {
          this.store.dispatch(ActionFailed({ error }))
          this.isProcessing = false
        },
      })
    } else {
      this.stepper.next()
    }
  }

  prevStep() {
    this.stepper.previous()
  }

  mergeTemplates() {
    const { uploaded } = this.templateBasicForm.getRawValue()

    const uploadedResults: TemplateUploadResponse[] = uploaded
    const urls = lodash.map(uploadedResults, (t) => t.pdf_file_url)
    if (urls.length > 1) {
      return this.fileService.merge(urls)
    } else {
      return of(lodash.head(urls))
    }
  }

  onPreview() {
    const { type } = this.templateBasicForm.getRawValue()
    const { font, font_size } = this.fieldsForm.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    this.previewDrawer.open({
      type,
      fields,
      pdfFileUrl: this.mergedTemplateFileUrl,
      defaultFontSize: font_size,
      defaultFont: font,
    })
  }

  validateStep() {
    switch (this.currentStep) {
      case CreateTemplateSteps.SelectTemplate:
        return validateForm(this.templateBasicForm)
      case CreateTemplateSteps.AddSigner:
        return validateForm(this.addSignersForm)
      case CreateTemplateSteps.PlaceFields:
        return this.placeFieldsFormCom.isTemplateReady()
      case CreateTemplateSteps.TemplateDetails:
        return validateForm(this.templateDetailsForm)
    }
  }

  showValidationMessage() {
    switch (this.currentStep) {
      case CreateTemplateSteps.SelectTemplate:
        this.toaster.warning($localize`Please upload your template file.`)
        return
      case CreateTemplateSteps.AddSigner:
        return
      case CreateTemplateSteps.PlaceFields:
        return this.toaster.warning($localize`To process to next step, please add Signature field for each role.`)
      case CreateTemplateSteps.TemplateDetails:
        return
    }
  }

  onSave() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }
    const { type, uploaded } = this.templateBasicForm.getRawValue()
    const { roles, signers } = getSignersFormData(this.addSignersForm)
    const { fields, font, font_size } = this.fieldsForm.getRawValue()
    const templateDetails = getTemplateDetailsFormData(this.templateDetailsForm)

    const currentUser = this.authService.currentUser
    const uploadedResults: TemplateUploadResponse[] = uploaded

    const payload: ContractTemplate = {
      ...templateDetails,
      origin_files: lodash.map(uploadedResults, (t) => t.origin),
      pdf_file_url: this.mergedTemplateFileUrl,
      type,
      roles,
      signers,
      fields,
      font,
      font_size,
      account: currentUser.account,
      user_id: this.authService.user.id,
    }

    this.store.dispatch(CreateContractTemplate({ payload }))
  }
}
